import React, { useMemo, useState, useContext } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Table, Loader, Chip, Tooltip } from '@ubeya/shared-web/components';
import useEmployeeApplications from '@ubeya/shared/hooks/account/useEmployeeApplications';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import useLabels from '@ubeya/shared/hooks/account/useLabels';
import usePositions from '@ubeya/shared/hooks/account/usePositions';
import useLocations from '@ubeya/shared/hooks/account/useLocations';
import { API_DATE_FORMAT } from '@ubeya/shared/constants';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { SORT_BY_DATE, SORT_BY_STRING, SORT_BY_TIME } from '@ubeya/shared/utils/sorting';
import useGetApplicationStatusConfig from './hooks/useGetApplicationStatusConfig';
import ShiftRenderer from 'timeSheet/components/CellRenderers/ShiftRenderer';

const StatusChip = styled(Chip)`
  min-width: 80px;
  text-align: center;
`;

const StatusRenderer = ({ rowData }) => {
  const { t } = useContext(ConfigContext);
  const { status, type } = rowData;
  const getStatusConfig = useGetApplicationStatusConfig();

  const { label, background, color } = getStatusConfig(status);

  // Show type if: status is not 'booked', OR status is 'booked' and type is 'ChangeShiftSpecificEmployee'
  const shownType = status !== 'booked' ? type : type === 'ChangeShiftSpecificEmployee' ? type : '';

  const tooltipBody = shownType ? `applicationStatus${shownType}` : '';

  return (
    <Tooltip body={t(tooltipBody)} disabled={!tooltipBody}>
      <StatusChip label={label} background={background} color={color} />
    </Tooltip>
  );
};

const ApplicationTable = ({ employeeId, timeRange, showRejectedOnly = false }) => {
  const { t } = useContext(ConfigContext);
  const { dateFormat, timeFormat } = useContext(ConfigContext);
  const { accountId } = useAccount();
  const { mappedLabels } = useLabels();
  const { mappedPositions } = usePositions();
  const { mappedLocations } = useLocations();
  const { isLoading, applications } = useEmployeeApplications({
    employeeId,
    timeRange,
    includeHistory: true,
    onlyRejected: showRejectedOnly,
    // Only include rejected applications when showing rejections tab
    ...(showRejectedOnly && { includeRejectedApplications: true })
  });
  const [sort, setSort] = useState({ sortBy: 'date', sortDirection: 'desc' });

  const columns = useMemo(
    () => [
      {
        Header: t('date'),
        dataKey: 'date',
        formatter: (date) => date && moment(date, API_DATE_FORMAT).format(`${dateFormat}, ddd`),
        sortable: true,
        sortFunc: SORT_BY_DATE('date')
      },
      { Header: t('location'), dataKey: 'location', sortable: true, sortFunc: SORT_BY_STRING('location') },
      {
        Header: t('shift'),
        dataKey: 'shiftId',
        Cell: ShiftRenderer,
        columnData: { accountId, mappedLabels, mappedJobTypes: mappedPositions, mappedLocations }
      },
      {
        Header: t('startTime'),
        dataKey: 'startTime',
        formatter: (startTime) => startTime && moment(startTime).format(timeFormat),
        sortable: true,
        sortFunc: SORT_BY_TIME('startTime')
      },
      {
        Header: t('endTime'),
        dataKey: 'endTime',
        formatter: (endTime) => endTime && moment(endTime).format(timeFormat),
        sortable: true,
        sortFunc: SORT_BY_TIME('endTime')
      },
      { Header: t('notes'), dataKey: 'notes', sortable: true, sortFunc: SORT_BY_STRING('notes') },
      // Show status column only in Applications tab (not in Rejections tab)
      ...(!showRejectedOnly
        ? [
            {
              Header: t('status'),
              dataKey: 'status',
              sortable: true,
              Cell: StatusRenderer,
              sortFunc: SORT_BY_STRING('status')
            }
          ]
        : [])
    ],
    [accountId, dateFormat, mappedLabels, mappedLocations, mappedPositions, showRejectedOnly, t, timeFormat]
  );

  const sortingColumns = useMemo(
    () =>
      columns
        .filter((column) => column && column.dataKey && column.sortFunc)
        .reduce((all, { dataKey, sortFunc }) => ({ ...all, [dataKey]: sortFunc }), {}),
    [columns]
  );

  const data = useMemo(() => {
    // Filter and map applications (includes both current and history with isHistory flag)
    // Backend handles onlyRejected filtering, we just filter out records without shift data
    return (applications || [])
      .filter(({ shift }) => !!shift)
      .map(({ id, shift, status, isHistory, type }) => {
        const {
          id: shiftId,
          projectId,
          branchId,
          date,
          startTime,
          endTime,
          labelId,
          positionId,
          location,
          name
        } = shift;

        return {
          id: isHistory ? `history-${id}` : id,
          shiftId,
          type,
          projectId,
          branchId,
          date,
          startTime,
          endTime,
          status,
          isHistory,
          notes: shift?.comments || '',
          location: location?.address || '',
          // Fields for ShiftRenderer
          labelId,
          locationId: location?.id,
          positionId,
          name
        };
      })
      .sort((a, b) =>
        sort.sortDirection === 'asc' ? sortingColumns?.[sort.sortBy]?.(a, b) : sortingColumns?.[sort.sortBy]?.(b, a)
      );
  }, [applications, sort.sortBy, sort.sortDirection, sortingColumns]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Table
      columns={columns}
      data={data}
      sortBy={sort.sortBy}
      sortDirection={sort.sortDirection}
      setSort={setSort}
      shadow={false}
      resizable
    />
  );
};

export default ApplicationTable;
